<template>
  <Modal
    :is-open="isOpen"
    :title="t('organization.agreementPrompt.title')"
    @close="dismiss"
  >
    <div class="agreement-prompt">
      <p class="agreement-prompt__intro">{{ t('organization.agreementPrompt.intro') }}</p>

      <ul class="agreement-prompt__list">
        <li
          v-for="agreement in outstanding"
          :key="agreement.document"
          class="agreement-prompt__item"
        >
          <a
            :href="agreement.url"
            target="_blank"
            rel="noopener noreferrer"
            class="agreement-prompt__link"
          >
            {{
              t('organization.agreementPrompt.edition', {
                document: t(DOCUMENT_LABEL_KEYS[agreement.document]),
                version: agreement.version
              })
            }}
          </a>
          <p class="agreement-prompt__summary">{{ agreement.summary }}</p>
        </li>
      </ul>

      <p class="agreement-prompt__hint">{{ t('organization.agreementPrompt.keepsWorking') }}</p>

      <Alert v-if="error" variant="error">{{ error }}</Alert>
    </div>

    <template #footer>
      <Button variant="secondary" :disabled="accepting" @click="dismiss">
        {{ t('organization.agreementPrompt.later') }}
      </Button>
      <Button variant="primary" :loading="accepting" @click="accept">
        {{ t('organization.agreementPrompt.accept') }}
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { AgreementDocument } from '@/api/generated-organizations'
import Modal from '@/components/organisms/Modal.vue'
import Button from '@/components/atoms/Button.vue'
import Alert from '@/components/atoms/Alert.vue'
import { useAgreementReacceptance } from '@/composables/useAgreementReacceptance'

/**
 * Asks a user who may change the practice's settings to accept the current version of each
 * agreement the practice is not yet bound by (#4494) — what changed, a link to the edition,
 * and *Akkoord*. Closing it any way other than *Akkoord* puts the question off until the next
 * sign-in; it never stands between anyone and their work. Who is asked, and when, is
 * {@link useAgreementReacceptance}'s.
 */

const { t } = useI18n()

/** The document names the signup form already uses for the same two documents. */
const DOCUMENT_LABEL_KEYS: Record<AgreementDocument, string> = {
  [AgreementDocument.Overeenkomst]: 'forms.organization.onboarding.agreements.overeenkomst',
  [AgreementDocument.Verwerkersovereenkomst]: 'forms.organization.onboarding.agreements.verwerkersovereenkomst'
}

const { isOpen, outstanding, accepting, error, accept, dismiss } = useAgreementReacceptance()
</script>

<style scoped>
.agreement-prompt {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.agreement-prompt__intro,
.agreement-prompt__hint,
.agreement-prompt__summary {
  margin: 0;
}

.agreement-prompt__list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin: 0;
  padding: 0;
  list-style: none;
}

.agreement-prompt__link {
  font-weight: var(--font-weight-semibold);
}

.agreement-prompt__summary,
.agreement-prompt__hint {
  color: var(--color-text-secondary);
}
</style>
